// deps
import { useCallback, useContext } from "react";

// internal-deps
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/js-utils";

// analytics
import { onInAppUpiHalfCardCtaClickEvent } from "@payment-client/analytics/events/InAppUpi";

// constants
import { INAPP_UPI_HALF_CARD_FREQUENCY } from "@payment-client/constants/InAppUpi";
import { APP_STORAGE_KEYS_FOR_INAPP_UPI_BANNER } from "@payment-client/constants/StorageKeys";

// hooks
import { useInAppUpiGroup } from "@payment-client/hooks/UseInAppUpiGroup";
import { useInAppUpiHalfCard } from "@payment-client/hooks/UseInAppUpiHalfCard";

// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

export const useInAppUPIHandler = (paymentResponse: GetPaymentMethodsResponse | undefined | null) => {
    const { clickEvent } = useContext(AnalyticsContext);

    // half card data
    const {
        getOneTapHalfCardFromAppStorage,
        showHalfCard,
        setShowHalfCard,
        ...inAppUpiHalfCardData
    } = useInAppUpiHalfCard();

    // sdk methods
    const { initiateOnboarding, onAddBankAccountClick } = useInAppUpiGroup();

    const initiateAndCloseHalfCard = useCallback(async () => {
        initiateOnboarding();
        inAppUpiHalfCardData.closeHalfCard();
    }, [inAppUpiHalfCardData, initiateOnboarding]);

    const canShowInAppUpiHalfCard = useCallback(async () => {
        const data = await getOneTapHalfCardFromAppStorage();
        return (
            Number(data[APP_STORAGE_KEYS_FOR_INAPP_UPI_BANNER.INAPP_UPI_HALF_CARD_COUNT]) <
            INAPP_UPI_HALF_CARD_FREQUENCY
        );
    }, [getOneTapHalfCardFromAppStorage]);

    const startInAppUpiOnboarding = useCallback(async () => {
        const canShowHalfCard = (await canShowInAppUpiHalfCard()) && !isEmpty(paymentResponse?.halfCardAwareness);
        if (canShowHalfCard) {
            setShowHalfCard(!showHalfCard);
            return;
        }
        initiateAndCloseHalfCard();
    }, [
        canShowInAppUpiHalfCard,
        initiateAndCloseHalfCard,
        paymentResponse?.halfCardAwareness,
        setShowHalfCard,
        showHalfCard,
    ]);

    const onInAppUpiHalfCardCtaClick = useCallback(() => {
        onInAppUpiHalfCardCtaClickEvent(clickEvent);
        initiateAndCloseHalfCard();
    }, [clickEvent, initiateAndCloseHalfCard]);

    return {
        onAddBankAccountClick,
        startInAppUpiOnboarding,
        inAppUpiHalfCardData: {
            showHalfCard,
            onInAppUpiHalfCardCtaClick,
            ...inAppUpiHalfCardData,
        },
    };
};
